<ng-container [formGroup]="linkForm.formGroup" *ngIf="linkForm.errors$ | async as errors">
  <div class="group first-group">
    <div class="inline-inputs-container">
      <div
        class="input-container"
        permission-aware-input-container="alias"
        *ngIf="!batch && !featuresToHide?.alias"
      >
        <label for="link-dialog-alias" trans>Custom Link ID</label>
        <input
          type="text"
          id="link-dialog-alias"
          formControlName="alias"
          [minlength]="alias.min"
          [maxlength]="alias.max"
          pattern="[A-Za-z0-9_-]+"
          [placeholder]="linkForm.linkHash.value"
        />
        <p class="error" *ngIf="errors.alias">{{ errors.alias }}</p>
      </div>
      <div class="input-container">
        <label for="link_dialog_active" trans>Status</label>
        <select id="link_dialog_active" formControlName="active">
          <option [ngValue]="true" trans>Activate</option>
          <option [ngValue]="false" trans>Disabled</option>
        </select>
      </div>
    </div>

    <div class="inline-inputs-container">
      <div class="input-container" permission-aware-input-container="password">
        <label for="link-dialog-password" trans>Password</label>
        <input
          type="password"
          id="link-dialog-password"
          formControlName="password"
          autocomplete="new-password"
        />
        <p class="error" *ngIf="errors.password">{{ errors.password }}</p>
      </div>
    </div>

    <permission-aware-button permission="links.create" restriction="utm">
      <button type="button" mat-stroked-button color="accent" (click)="openUtmBuilder()" trans>
        UTM Builder
      </button>
    </permission-aware-button>
  </div>

  <div
    class="group"
    *ngIf="
      linkOptions?.domains?.length &&
      !featuresToHide?.domain &&
      this.settings.get('custom_domains.allow_select')
    "
  >
    <header>
      <div class="header-text">
        <h3 trans>Domain</h3>
        <p trans>Select on which domain should this short link be accessible.</p>
      </div>
    </header>
    <domain-form-control
      [domains]="linkOptions.domains"
      controlName="domain_id"
      [hideLabel]="true"
    ></domain-form-control>
  </div>

  <div class="group">
    <header>
      <div class="header-text">
        <h3 trans>Schedule</h3>
        <p [transValues]="{resource: resourceName}" trans>
          Enter a date when :resource should be active. Both start and end dates are optional.
        </p>
      </div>
    </header>
    <div class="inline-inputs-container">
      <div class="input-container" permission-aware-input-container="expiration">
        <datetime-input id="link_dialog_activates_at" formControlName="activates_at" trans
          >Start Date</datetime-input
        >
        <p class="error" *ngIf="errors.activates_at">{{ errors.activates_at }}</p>
      </div>
      <div class="input-container" permission-aware-input-container="expiration">
        <datetime-input id="link_dialog_expires_at" formControlName="expires_at" trans
          >End Date</datetime-input
        >
        <p class="error" *ngIf="errors.expires_at">{{ errors.expires_at }}</p>
      </div>
    </div>
  </div>

  <div class="group" *ngIf="!featuresToHide?.linkGroups && linkOptions?.groups?.length">
    <header>
      <div class="header-text">
        <h3 trans>Link Groups</h3>
        <p trans>Groups this link should be attached to.</p>
      </div>
    </header>
    <mat-form-field appearance="outline">
      <mat-label trans>Select Groups</mat-label>
      <mat-select formControlName="groups" multiple>
        <mat-option *ngFor="let group of linkOptions.groups" [value]="group.id">{{
          capitalize(group.name)
        }}</mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <div class="group many-inputs" *ngIf="!featuresToHide?.details">
    <header>
      <div class="header-text">
        <h3 trans>Details</h3>
        <p trans>Add title, description and upload an image to personalize the link.</p>
      </div>
    </header>
    <div class="inline-inputs-container">
      <upload-image-control formControlName="image" [compact]="true"></upload-image-control>
      <div class="input-container">
        <label for="link_dialog_title" trans>Title</label>
        <input type="text" id="link_dialog_title" formControlName="name" maxlength="140" />
        <p class="error" *ngIf="errors.name">{{ errors.name }}</p>
      </div>
    </div>

    <div class="input-container" *ngIf="!featuresToHide?.tags">
      <label for="link_dialog_tags" trans>Tags</label>
      <chip-input
        id="link_dialog_tags"
        formControlName="tags"
        placeholder="+New tag"
        trans-placeholder
      ></chip-input>
    </div>

    <div class="input-container">
      <label for="link_dialog_description" trans>Description</label>
      <textarea
        id="link_dialog_description"
        formControlName="description"
        maxlength="240"
      ></textarea>
      <p class="error" *ngIf="errors.description">{{ errors.description }}</p>
    </div>
  </div>

  <div class="group" *ngIf="linkOptions?.pixels?.length && this.settings.get('links.pixels')">
    <header>
      <div class="header-text">
        <h3 trans>Tracking Pixel</h3>
        <p trans>Select tracking pixels that should be used for this link.</p>
      </div>
    </header>
    <mat-form-field appearance="outline">
      <mat-label trans>Select Pixels</mat-label>
      <mat-select formControlName="pixels" multiple>
        <mat-option *ngFor="let pixel of linkOptions.pixels" [value]="pixel.id">{{
          capitalize(pixel.name)
        }}</mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <div
    class="group retargeting-group"
    permission-aware-input-container="expiration"
    [controlGroup]="linkForm.expClicksRule"
  >
    <header>
      <div class="header-text">
        <h3 trans>Expiration Clicks</h3>
        <p>
          <span [transValues]="{resource: resourceName}" trans
            >After :resource is visited specified amount of times, it will no longer be accessible.</span
          ><br /><span [transValues]="{resource: resourceName}" trans
            >Optionally, after click amount is reached :resource can redirect to specified url
            instead.</span
          >
        </p>
      </div>
    </header>
    <div class="inline-inputs-container rule-inline-inputs" formGroupName="exp_clicks_rule">
      <div class="input-container country-input-container">
        <label for="link_dialog_exp_clicks" trans>Max Clicks</label>
        <input type="number" min="1" id="link_dialog_exp_clicks" formControlName="key" />
      </div>

      <div class="input-container">
        <label for="link_dialog_exp_link" trans>Redirect Url</label>
        <input
          type="url"
          id="link_dialog_exp_link"
          formControlName="value"
          placeholder="(Optional)"
          trans-placeholder
        />
      </div>
    </div>
  </div>

  <ng-container *ngIf="linkOptions && settings.get('links.retargeting')">
    <div
      class="group retargeting-group"
      permission-aware-input-container="retargeting"
      [controlGroup]="linkForm.geoRules"
    >
      <header>
        <div class="header-text">
          <h3 trans>Location Targeting</h3>
          <p trans>Redirect users to different url based on their location.</p>
        </div>
        <button
          mat-icon-button
          color="accent"
          type="button"
          (click)="linkForm.addNewGeoRule()"
          *ngIf="!linkForm.geoRules.disabled"
        >
          <mat-icon svgIcon="add"></mat-icon>
        </button>
      </header>
      <ul class="unstyled-list">
        <li
          class="inline-inputs-container rule-inline-inputs"
          *ngFor="let group of linkForm.geoRules.controls; let i = index"
        >
          <div class="input-container country-input-container" [formGroup]="group">
            <label [for]="'link_dialog_country-' + i" trans>Country</label>
            <select [id]="'link_dialog_country-' + i" formControlName="key">
              <option [value]="country.code" *ngFor="let country of linkOptions.countries">
                {{ country.name }}
              </option>
            </select>
          </div>

          <div class="input-container" [formGroup]="group">
            <label [for]="'link_dialog_geo_url-' + i" trans>Url</label>
            <input type="url" [id]="'link_dialog_geo_url-' + i" formControlName="value" />
          </div>

          <button
            mat-icon-button
            color="warn"
            type="button"
            class="remove-rule-btn"
            [disabled]="group.disabled"
            (click)="linkForm.geoRules.removeAt(i)"
          >
            <mat-icon svgIcon="close"></mat-icon>
          </button>
        </li>
      </ul>
    </div>

    <div
      class="group retargeting-group"
      permission-aware-input-container="retargeting"
      [controlGroup]="linkForm.deviceRules"
    >
      <header>
        <div class="header-text">
          <h3 trans>Device Targeting</h3>
          <p trans>Redirect users to different url based on their device.</p>
        </div>
        <button
          mat-icon-button
          color="accent"
          type="button"
          (click)="linkForm.addNewDeviceRule()"
          *ngIf="!linkForm.geoRules.disabled"
        >
          <mat-icon svgIcon="add"></mat-icon>
        </button>
      </header>
      <ul class="unstyled-list">
        <li
          class="inline-inputs-container rule-inline-inputs"
          *ngFor="let group of linkForm.deviceRules.controls; let i = index"
        >
          <div class="input-container device-input-container" [formGroup]="group">
            <label [for]="'link_dialog_device-' + i" trans>Device</label>
            <select [id]="'link_dialog_device-' + i" formControlName="key">
              <option value="desktop" trans>Desktop</option>
              <option value="tablet" trans>Tablet</option>
              <option value="mobile" trans>Mobile</option>
            </select>
          </div>

          <div class="input-container" [formGroup]="group">
            <label [for]="'link_dialog_device_url-' + i" trans>Url</label>
            <input type="url" [id]="'link_dialog_device_url-' + i" formControlName="value" />
          </div>

          <button
            mat-icon-button
            type="button"
            color="warn"
            class="remove-rule-btn"
            [disabled]="group.disabled"
            (click)="linkForm.deviceRules.removeAt(i)"
          >
            <mat-icon svgIcon="close"></mat-icon>
          </button>
        </li>
      </ul>
    </div>

    <div
      class="group retargeting-group"
      permission-aware-input-container="retargeting"
      [controlGroup]="linkForm.platformRules"
    >
      <header>
        <div class="header-text">
          <h3 trans>Platform Targeting</h3>
          <p trans>Redirect users to different url based on their platform.</p>
        </div>
        <button
          mat-icon-button
          color="accent"
          type="button"
          (click)="linkForm.addNewPlatformRule()"
          *ngIf="!linkForm.geoRules.disabled"
        >
          <mat-icon svgIcon="add"></mat-icon>
        </button>
      </header>
      <ul class="unstyled-list">
        <li
          class="inline-inputs-container rule-inline-inputs"
          *ngFor="let group of linkForm.platformRules.controls; let i = index"
        >
          <div class="input-container device-input-container" [formGroup]="group">
            <label [for]="'link_dialog_platform-' + i" trans>Platform</label>
            <select [id]="'link_dialog_platform-' + i" formControlName="key">
              <option value="windows" trans>Windows</option>
              <option value="linux" trans>Linux</option>
              <option value="osx" trans>MacOS</option>
              <option value="ios" trans>iOS</option>
              <option value="androidos" trans>Android</option>
            </select>
          </div>

          <div class="input-container" [formGroup]="group">
            <label [for]="'link_dialog_platform_url-' + i" trans>Url</label>
            <input type="url" [id]="'link_dialog_platform_url-' + i" formControlName="value" />
          </div>

          <button
            mat-icon-button
            type="button"
            color="warn"
            class="remove-rule-btn"
            [disabled]="group.disabled"
            (click)="linkForm.platformRules.removeAt(i)"
          >
            <mat-icon svgIcon="close"></mat-icon>
          </button>
        </li>
      </ul>
    </div>
  </ng-container>
</ng-container>
